import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import QolProbabilitiesChart from "@/components/QolProbabilitiesChart";
import BrainTypeField from "@/components/BrainTypeField";
import { Button } from "@/components/ui/button";
import { ArrowLeft, UserCircle2, Mail, CheckCircle2, Circle, Brain, Check } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "perfect", label: "Perfect Days" },
  { key: "very_good", label: "Very Good Days" },
  { key: "good", label: "Good Days" },
  { key: "okayish", label: "Okayish Days" },
  { key: "bad", label: "Bad Days" },
  { key: "very_bad", label: "Very Bad Days" },
];

const QUESTIONS = [
  { key: "emotions", label: "Emotions & Feelings" },
  { key: "past", label: "Dealing with the Past" },
  { key: "future", label: "Dealing with the Future" },
  { key: "inner_self_talk", label: "Inner Self Talk" },
];

export default function UserDetail() {
  const { id } = useParams();
  const { user: actor } = useAuth();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);

  const [brainType, setBrainType] = useState(null);
  const [savingBT, setSavingBT] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${id}`);
        setUser(data.user);
        // Pre-fill the editor with the definitive brain type, falling back to the
        // preliminary self-test result so the coach can confirm with one click.
        setBrainType(data.user.brain_type ?? data.user.preliminary_brain_type ?? null);
        setOnboarding(data.onboarding);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, navigate]);

  const backHref = useMemo(() => {
    if (!actor || !user) return "/";
    if (actor.role === "super_admin" && user.coach_id) return `/details/coach/${user.coach_id}`;
    if (actor.role === "institute_admin" && user.coach_id) return `/details/coach/${user.coach_id}`;
    if (actor.role === "coach") return "/coach/users";
    return "/";
  }, [actor, user]);

  const btDirty = (user?.brain_type ?? null) !== brainType;

  const saveBrainType = async () => {
    setSavingBT(true);
    try {
      const { data } = await api.put(`/users/${id}`, { brain_type: brainType });
      setUser(data);
      toast.success("Brain type saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingBT(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="px-8 lg:px-12 py-12">
        <p className="text-sm text-[#8A9188] uppercase tracking-[0.2em] font-bold">Loading…</p>
      </div>
    );
  }

  return (
    <div className="px-8 lg:px-12 py-12 max-w-7xl" data-testid="user-detail-page">
      <Link
        to={backHref}
        data-testid="user-detail-back"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#8A9188] hover:text-[#1A1D1A] font-bold mb-6"
      >
        <ArrowLeft strokeWidth={1.5} size={14} />
        Back
      </Link>

      <PageHeader overline="Client" title={user.name} description={user.email} />

      {/* Profile + Brain Type editor */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <Stat icon={UserCircle2} label="Name" value={user.name} />
        <Stat icon={Mail} label="Email" value={user.email} />
        <Stat
          icon={user.onboarding_completed ? CheckCircle2 : Circle}
          label="Onboarding"
          value={user.onboarding_completed ? "Complete" : "Pending"}
          hint={`Joined ${new Date(user.created_at).toLocaleDateString()}`}
        />
      </div>

      <div
        className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 mb-10"
        data-testid="user-detail-brain-type-editor"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="flex items-start gap-4 max-w-xl">
            <div className="h-11 w-11 rounded-xl bg-[#F2F0EA] flex items-center justify-center shrink-0">
              <Brain strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9188]">
                Brain type
              </p>
              <h3 className="font-heading text-xl sm:text-2xl text-[#1A1D1A] tracking-tight font-semibold mt-1">
                {user.brain_type ?? "Not set"}
              </h3>
              {user.brain_type == null && user.preliminary_brain_type != null && (
                <p
                  className="text-sm text-[#5C635A] mt-2 leading-relaxed"
                  data-testid="user-detail-preliminary-brain-type"
                >
                  Quick-test result during onboarding:{" "}
                  <span className="font-semibold text-[#1A1D1A]">
                    voorlopig type #{user.preliminary_brain_type}
                  </span>
                  . That self-test is reliable about 2 out of 3 times (~66%) — confirm or override it
                  below after your consultation with the client.
                </p>
              )}
              {user.brain_type == null && user.preliminary_brain_type == null && (
                <p className="text-sm text-[#5C635A] mt-2 leading-relaxed">
                  This client hasn't taken the quick brain-type self-test yet.
                </p>
              )}
              <p className="text-sm text-[#5C635A] mt-2 leading-relaxed">
                A number from 1 to 9. Set or update the client's brain type below — it saves immediately on Save.
              </p>
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div className="w-36">
              <BrainTypeField
                value={brainType}
                onChange={setBrainType}
                testid="user-detail-brain-type-input"
                label="Brain Type (1–9)"
              />
            </div>
            <Button
              onClick={saveBrainType}
              disabled={!btDirty || savingBT}
              data-testid="user-detail-brain-type-save"
              className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
            >
              <Check strokeWidth={1.5} size={18} className="mr-2" />
              {savingBT ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </div>

      {/* QoL probability chart — primary focus, top of body */}
      <QolProbabilitiesChart userId={id} />

      {/* Onboarding response — secondary */}
      <h2 className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold mt-12 mb-4">
        Onboarding response — 1000 days before coaching
      </h2>
      {onboarding ? (
        <div
          className="bg-white border border-[#E5E2DC] rounded-2xl overflow-hidden"
          data-testid="user-detail-onboarding"
        >
          <div className="grid grid-cols-8 px-6 py-4 text-xs uppercase tracking-[0.2em] text-[#8A9188] font-bold border-b border-[#E5E2DC]">
            <div className="col-span-2">Question</div>
            {CATEGORIES.map((c) => (
              <div key={c.key} className="text-right">
                {c.label.split(" ")[0]}
              </div>
            ))}
          </div>
          {QUESTIONS.map((q) => {
            const scores = onboarding[q.key] || {};
            return (
              <div
                key={q.key}
                className="grid grid-cols-8 px-6 py-5 border-b last:border-0 border-[#E5E2DC] items-center"
              >
                <div className="col-span-2 font-medium text-[#1A1D1A]">{q.label}</div>
                {CATEGORIES.map((c) => (
                  <div
                    key={c.key}
                    className="text-right font-heading text-lg text-[#1A1D1A] font-light"
                  >
                    {scores[c.key] ?? 0}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      ) : (
        <div
          className="bg-[#F2F0EA] border border-[#E5E2DC] rounded-2xl p-8 text-center"
          data-testid="user-detail-no-onboarding"
        >
          <Circle strokeWidth={1.5} className="mx-auto text-[#B37A5F] mb-3" size={28} />
          <p className="text-[#5C635A]">This client hasn't completed their onboarding yet.</p>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="bg-white border border-[#E5E2DC] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <Icon strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#8A9188] font-bold">{label}</span>
      </div>
      <p className="font-heading text-2xl tracking-tight text-[#1A1D1A] font-semibold truncate">{value}</p>
      {hint && <p className="text-xs text-[#8A9188] mt-2 truncate">{hint}</p>}
    </div>
  );
}
